// src/components/dashboard/Workshop1Dashboard.js
// Professional Workshop 1 Dashboard using Tremor components (Light Theme)
import React from 'react';
import {
    Card,
    Title,
    Text,
    Metric,
    Grid,
    DonutChart,
    Flex,
    Legend,
    Badge,
    BadgeDelta
} from "@tremor/react";
import { useTranslation } from 'react-i18next';
import {
    RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
    ResponsiveContainer
} from 'recharts';
import {
    Users,
    Shield,
    AlertTriangle,
    Briefcase
} from 'lucide-react';

// Color mapping for charts (light theme colors)
const statusColors = {
    "En place": "emerald",
    "Partiel": "blue",
    "Planifié": "amber",
    "À appliquer avant le": "violet",
    "À abandonner": "rose",
    "Implemented": "emerald",
    "Partial": "blue",
    "Planned": "amber",
    "To Apply": "violet",
    "To Abandon": "rose"
};

const Workshop1Dashboard = ({
    contextData = {},
    businessValues = [],
    dreadedEvents = [],
    securityFramework = {},
    securityControlStatusData = [],
    controlsData = [],
    participantRolesData = [],
    businessValuePillarsData = [],
    eventsBySeverityData = [],
    securityPillarEventsData = []
}) => {
    const { t } = useTranslation();

    // Process Participants
    const participants = contextData?.participants || [];

    // Process roles data for donut chart
    const rolesChartData = participantRolesData.length > 0
        ? participantRolesData
        : participants.reduce((acc, p) => {
            const role = p.position || p.role || t('common.unknown');
            const existing = acc.find(item => item.name === role);
            if (existing) existing.value += 1;
            else acc.push({ name: role, value: 1 });
            return acc;
        }, []);

    // Process Dreaded Events by Severity for bar list
    const severityData = eventsBySeverityData.length > 0
        ? eventsBySeverityData.map(item => ({
            name: item.name,
            value: item.value
        }))
        : dreadedEvents.reduce((acc, event) => {
            const sev = event.severity || event.gravity || t('common.unknown');
            const existing = acc.find(item => item.name === sev);
            if (existing) existing.value += 1;
            else acc.push({ name: sev, value: 1 });
            return acc;
        }, []);

    // Process Security Pillars from Business Values
    const pillarsData = businessValuePillarsData.length > 0
        ? businessValuePillarsData
        : businessValues.reduce((acc, bv) => {
            if (Array.isArray(bv.securityPillars)) {
                bv.securityPillars.forEach(p => {
                    const existing = acc.find(item => item.name === p);
                    if (existing) existing.value += 1;
                    else acc.push({ name: p, value: 1 });
                });
            }
            return acc;
        }, []);

    // Process severity data for moved donut chart
    const severityChartData = eventsBySeverityData.map(item => ({
        name: item.name,
        value: item.value
    }));

    // Process pillar data for moved donut chart
    const pillarChartData = securityPillarEventsData.map(item => ({
        name: item.subject || item.name,
        value: item.value
    })).filter(item => item.value > 0);

    const totalControls = securityControlStatusData.reduce((sum, item) => sum + item.value, 0);
    const implementedCount = securityControlStatusData.find(s =>
        s.name === 'En place' || s.name === 'Implemented' ||
        s.name === t('dashboard.statusLabels.implemented')
    )?.value || 0;

    // Transform security status data for donut chart
    const statusChartData = securityControlStatusData.map(item => ({
        name: item.name,
        value: item.value
    }));

    // Get status colors array for legend
    const statusColorArray = securityControlStatusData.map(item =>
        statusColors[item.name] || "slate"
    );

    // Count critical events
    const criticalCount = severityData.filter(s =>
        s.name === 'Critique' || s.name === 'Catastrophique' ||
        s.name === 'Critical' || s.name === 'Catastrophic'
    ).reduce((sum, s) => sum + s.value, 0);

    return (
        // Force light theme wrapper
        <div className="space-y-6 bg-gray-50 p-4 rounded-xl">
            {/* KPI Cards Row */}
            <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
                {/* Participants Card */}
                <Card decoration="top" decorationColor="blue" className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.cards.participants')}</Text>
                            <Metric className="!text-gray-900">{participants.length}</Metric>
                            <Text className="mt-1 text-xs !text-gray-500">
                                {rolesChartData.length} {t('dashboard.cards.differentRoles')}
                            </Text>
                        </div>
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Users className="w-6 h-6 text-blue-600" />
                        </div>
                    </Flex>
                </Card>

                {/* Business Values Card */}
                <Card decoration="top" decorationColor="violet" className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.cards.businessValues')}</Text>
                            <Metric className="!text-gray-900">{businessValues.length}</Metric>
                            <Text className="mt-1 text-xs !text-gray-500">
                                {pillarsData.length} {t('dashboard.cards.pillarsCovered')}
                            </Text>
                        </div>
                        <div className="p-2 bg-violet-100 rounded-lg">
                            <Briefcase className="w-6 h-6 text-violet-600" />
                        </div>
                    </Flex>
                </Card>

                {/* Dreaded Events Card */}
                <Card decoration="top" decorationColor="rose" className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.cards.dreadedEvents')}</Text>
                            <Metric className="!text-gray-900">{dreadedEvents.length}</Metric>
                            {criticalCount > 0 && (
                                <BadgeDelta deltaType="increase" size="xs" className="mt-1">
                                    {criticalCount} {t('dashboard.cards.critical')}
                                </BadgeDelta>
                            )}
                        </div>
                        <div className="p-2 bg-rose-100 rounded-lg">
                            <AlertTriangle className="w-6 h-6 text-rose-600" />
                        </div>
                    </Flex>
                </Card>

                {/* Security Measures Card */}
                <Card decoration="top" decorationColor="emerald" className="!bg-white">
                    <Flex alignItems="start" justifyContent="between">
                        <div>
                            <Text className="!text-gray-600">{t('dashboard.cards.securityMeasures')}</Text>
                            <Metric className="!text-gray-900">{totalControls}</Metric>
                            <Flex className="mt-1" justifyContent="start" alignItems="center">
                                <Badge color="emerald" size="xs">
                                    {implementedCount} {t('dashboard.cards.implemented')}
                                </Badge>
                            </Flex>
                        </div>
                        <div className="p-2 bg-emerald-100 rounded-lg">
                            <Shield className="w-6 h-6 text-emerald-600" />
                        </div>
                    </Flex>
                </Card>
            </Grid>

            {/* Charts Grid */}
            <Grid numItems={1} numItemsLg={2} className="gap-6">
                {/* Events by Pillar - Radar Chart */}
                <Card className="!bg-white">
                    <Title className="!text-gray-900">{t('dashboard.workshop2.eventsByPillar')}</Title>
                    {pillarChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <RadarChart data={pillarChartData}>
                                <PolarGrid />
                                <PolarAngleAxis dataKey="name" tick={{ fontSize: 11 }} />
                                <PolarRadiusAxis tick={false} axisLine={false} />
                                <Radar
                                    dataKey="value"
                                    stroke="#8B5CF6"
                                    fill="#8B5CF6"
                                    fillOpacity={0.3}
                                />
                            </RadarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-60 flex items-center justify-center">
                            <Text className="!text-gray-400">{t('dreadedEvents.empty.noEvents')}</Text>
                        </div>
                    )}
                </Card>

                {/* Events by Severity - Donut Chart (moved from Workshop 2) */}
                <Card className="!bg-white">
                    <Title className="!text-gray-900">{t('dashboard.workshop2.eventsBySeverity')}</Title>
                    {severityChartData.length > 0 ? (
                        <>
                            <DonutChart
                                className="mt-6 h-60"
                                data={severityChartData}
                                category="value"
                                index="name"
                                colors={["slate", "rose", "orange", "amber", "emerald", "gray"]}
                                showAnimation={true}
                                showTooltip={true}
                            />
                            <Legend
                                categories={severityChartData.map(d => d.name)}
                                colors={["slate", "rose", "orange", "amber", "emerald", "gray"]}
                                className="mt-4"
                            />
                        </>
                    ) : (
                        <div className="h-60 flex items-center justify-center">
                            <Text className="!text-gray-400">{t('dreadedEvents.empty.noEvents')}</Text>
                        </div>
                    )}
                </Card>

                {/* Security Measures Status - Donut Chart */}
                <Card className="!bg-white">
                    <Title className="!text-gray-900">{t('dashboard.charts.securityDistribution')}</Title>
                    {statusChartData.length > 0 ? (
                        <>
                            <DonutChart
                                className="mt-6 h-60"
                                data={statusChartData}
                                category="value"
                                index="name"
                                colors={statusColorArray}
                                showAnimation={true}
                                showTooltip={true}
                            />
                            <Legend
                                categories={statusChartData.map(d => d.name)}
                                colors={statusColorArray}
                                className="mt-4"
                            />
                        </>
                    ) : (
                        <div className="h-60 flex items-center justify-center">
                            <Text className="!text-gray-400">{t('dashboard.empty.noSecurityMeasures')}</Text>
                        </div>
                    )}
                </Card>

                {/* Implementation Timeline - Bar Chart */}
                {controlsData.length > 0 && (() => {
                    const now = new Date();
                    const oneMonthFromNow = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
                    const threeMonthsFromNow = new Date(now.getFullYear(), now.getMonth() + 3, now.getDate());

                    const shortTerm = controlsData.filter(c => {
                        if (c.status === 'implemented') return false;
                        if (c.implementationDate) {
                            const implDate = new Date(c.implementationDate);
                            return implDate <= oneMonthFromNow;
                        }
                        return c.priority === 'high' || c.status === 'apply_before';
                    }).length;

                    const mediumTerm = controlsData.filter(c => {
                        if (c.status === 'implemented') return false;
                        if (c.implementationDate) {
                            const implDate = new Date(c.implementationDate);
                            return implDate > oneMonthFromNow && implDate <= threeMonthsFromNow;
                        }
                        return c.priority === 'medium' || c.status === 'planned';
                    }).length;

                    const longTerm = controlsData.filter(c => {
                        if (c.status === 'implemented') return false;
                        if (c.implementationDate) {
                            const implDate = new Date(c.implementationDate);
                            return implDate > threeMonthsFromNow;
                        }
                        return c.priority === 'low' || c.status === 'partial';
                    }).length;

                    const timelineChartData = [
                        { period: `≤ 1 ${t('common.month')}`, count: shortTerm, fill: '#F43F5E' },
                        { period: `1-3 ${t('common.months')}`, count: mediumTerm, fill: '#F59E0B' },
                        { period: `> 3 ${t('common.months')}`, count: longTerm, fill: '#10B981' },
                    ];

                    return (
                        <Card className="!bg-white">
                            <Title className="!text-gray-900">{t('dashboard.workshop1.implementationTimeline')}</Title>
                            <ResponsiveContainer width="100%" height={250}>
                                <BarChart data={timelineChartData} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                                    <XAxis dataKey="period" tick={{ fontSize: 12, fill: '#6B7280' }} />
                                    <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: '#6B7280' }} />
                                    <Tooltip
                                        contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB' }}
                                    />
                                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                                        {timelineChartData.map((entry, index) => (
                                            <Cell key={index} fill={entry.fill} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Card>
                    );
                })()}
            </Grid>
        </div>
    );
};

export default Workshop1Dashboard;
